import { UrlTree } from '@angular/router';
import { lastValueFrom, of } from 'rxjs';
import { mergeMap, take } from 'rxjs/operators';
import { wrapIntoObservable } from '../utils';
import { JssCanActivateRedirectError } from './jss-can-activate-error';
/**
 * @param {boolean | string | string[] | UrlTree} value
 * @returns instance of value
 */
function isRedirectValue(value) {
    return value instanceof UrlTree || typeof value === 'string' || Array.isArray(value);
}
/**
 * Resolves components based on provided guards
 * @param {Injector} injector
 * @param {ActivatedRoute} activatedRoute
 * @param {Router} router
 * @returns {(factories: ComponentFactoryResult[]) => Promise<ComponentFactoryResult[]>} factory
 */
export function guardResolverFactory(injector, activatedRoute, router) {
    /**
     * @param {JssCanActivate | Type<JssCanActivate> | JssCanActivateFn} guard
     * @returns resolved guard
     */
    function getGuardInstance(guard) {
        if ('canActivate' in guard) {
            return guard;
        }
        try {
            // Class is provided
            return injector.get(guard);
        }
        catch {
            // Function is provided
            return guard;
        }
    }
    /**
     * @param {ComponentFactoryResult} factory
     * @returns {(JssCanActivate | JssCanActivateFn)[]} guard instances
     */
    function collectGuardInstances(factory) {
        if (factory.canActivate) {
            return Array.isArray(factory.canActivate)
                ? factory.canActivate.map(getGuardInstance)
                : [getGuardInstance(factory.canActivate)];
        }
        return [];
    }
    /**
     *
     * @param {JssCanActivate | JssCanActivateFn} guard
     * @param {ComponentFactoryResult} factory
     * @returns canActivate
     */
    function resolveGuard(guard, factory) {
        const canActivate = 'canActivate' in guard ? guard.canActivate.bind(guard) : guard;
        const guardValue = canActivate({
            activatedRoute: activatedRoute.snapshot,
            routerState: router.routerState.snapshot,
            rendering: factory.componentDefinition,
        });
        const canActivate$ = wrapIntoObservable(guardValue);
        return lastValueFrom(canActivate$.pipe(take(1), mergeMap((value) => {
            if (isRedirectValue(value)) {
                throw new JssCanActivateRedirectError(`Value: '${value.toString()}' is a redirect value`, value);
            }
            else {
                return of(value);
            }
        })));
    }
    return function resolveGuards(factories) {
        const resolved = factories.map((factory) => {
            const guards = collectGuardInstances(factory);
            const pending = guards.map((guard) => resolveGuard(guard, factory));
            return Promise.all(pending)
                .then((canActive) => canActive.every((v) => v))
                .then((canActivate) => ({
                factory,
                canActivate,
            }));
        });
        return Promise.all(resolved).then((mapped) => mapped.filter((m) => m.canActivate).map((m) => m.factory));
    };
}
